import {
    GetBarsResult,
    PeriodParamsWithOptionalCountback
} from "../../../../vendor/tradeview/datafeeds/udf/src/history-provider";
import {Bar, LibrarySymbolInfo, ResolutionString} from "../../../../vendor/tradeview/charting_library/datafeed-api";
import {parseFullSymbol1} from "../datafeeds/udf/src/helper";
import {extractIntervalAndPeriod, unixTimestampToDate} from "../helper/util";
import {Interval, TimeUnit} from "../types/DataFeedTypes";
import {StringKeyed} from "../../../types/GenericTypes";
import {IHttpService} from "angular";
import {Optional} from "../../../js/util/Optional";
import moment, {Duration} from "moment/moment";
import {Moment} from "moment-timezone";
import {unitOfTime} from "moment";
import {getErrorMessage, RequestParams} from "../helper/helpers";

declare const Papa: any;

export class TvHistoryProvider {

    public static id = "tvHistoryProvider";

    private readonly isoDateFormat = "YYYY-MM-DD";
    private readonly isoDateFormatWithTime = "YYYY-MM-DDTHH:mm:ss";

    private readonly max_periods: StringKeyed<Duration> = {
        day: moment.duration(600, "d"),
        minute: moment.duration(7, "d"),
    };
    private $http: IHttpService;

    /** @ngInject */
    public constructor(
        $http: IHttpService
    ) {
        this.$http = $http;

    }

    protected async retrieveData(symbol: string,
                                 startDate: Date,
                                 endDate: Date | null,
                                 period: number | null,
                                 i: Interval | null,
                                 countBack?: number | null): Promise<Bar[]> {
        const params = this.generateRequestParams(startDate, endDate, period, i, countBack);
        const raw = await this.requestData(symbol, params);
        const csv = this.parseCsv(raw);
        return this.toSeries(csv, i);
    }

    private async requestData(symbol: string, params: StringKeyed<string>): Promise<string> {
        const config = {
            params,
            ignoreLoadingBar: true
        };
        try {
            const response = await this.$http.get<string>(`/tradefloor/api/prices/chart/${symbol}`, config);
            return response.data!;
        } catch (error) {
            return Promise.resolve("");
        }
    }

    // TODO This needs to be done a lot better, a lot of this logic could do with being moved up a bit into the base
    /* An end date of "null" means "give me all data from [startDate] until now" */
    private generateRequestParams(startDate: Date,
                                  endDate: Date | null,
                                  period: number | null,
                                  interval: Interval | TimeUnit | null,
                                  countBack?: number | null): StringKeyed {

        const params: StringKeyed<any> = {};
        const chartInterval = Optional.of(interval)
                                      .orElse("day") as unitOfTime.Base;
        const maxPeriod = Optional.of(this.max_periods[chartInterval])
                                  .orElse(this.max_periods["day"]);
        let toDate: Moment;

        if (endDate == null) {
            if (chartInterval === "minute") {
                toDate = moment().tz("Australia/Sydney"); // up to date now.
            } else {
                toDate = moment(startDate).add(period!, chartInterval);
            }
        } else {
            toDate = moment(endDate);
        }

        // const fromDate = moment(startDate);
        let fromDate = moment(startDate);
        const earliestFromDate = toDate.clone().subtract(maxPeriod);
        if (earliestFromDate.isAfter(fromDate)) {
            fromDate = earliestFromDate;
        }

        if (chartInterval !== "minute" && countBack && countBack > 300) {
            fromDate.subtract(5, "days");
        }

        const dateTimeFormat = interval === "minute" ? this.isoDateFormatWithTime : this.isoDateFormat;
        params.from = fromDate.format(dateTimeFormat);
        params.to = toDate.format(dateTimeFormat);
        if (interval === "minute") {
            params.i = period!;
        }

        return params;
    }

    private parseCsv(raw: string): Array<Array<string>> {
        const rows = Papa.parse(raw).data;
        rows.shift(); // remove the header row

        const rowHasData = (row: Array<string>): boolean => {
            return row != null && row.length > 0 && row[0] != null && row[0] !== "";
        };

        return rows.filter(rowHasData);
    }

    private toSeries(data: Array<Array<string>>, interval: Interval | null): Bar[] {
        return data.map(entry => {
            const date = new Date(entry[0]).getTime();
            return {
                time: date,
                open: parseFloat(entry[1]),
                high: parseFloat(entry[2]),
                low: parseFloat(entry[3]),
                close: parseFloat(entry[4]),
                volume: parseFloat(entry[7])
            };
        });
    }

    public getBars(symbolInfo: LibrarySymbolInfo,
                   resolution: string | ResolutionString,
                   periodParams: PeriodParamsWithOptionalCountback): Promise<GetBarsResult> {
        const requestParams: RequestParams = {
            symbol: symbolInfo.ticker || "",
            resolution,
            from: periodParams.from,
            to: periodParams.to,
        };
        if (periodParams.countBack !== undefined) {
            requestParams.countback = periodParams.countBack;
        }

        if (symbolInfo.currency_code !== undefined) {
            requestParams.currencyCode = symbolInfo.currency_code;
        }

        if (symbolInfo.unit_id !== undefined) {
            requestParams.unitId = symbolInfo.unit_id;
        }

        return new Promise(
            async (
                resolve: (result: GetBarsResult) => void,
                reject: (reason: string) => void
            ) => {

                try {
                    const parsedSymbol = parseFullSymbol1(symbolInfo.ticker ?? "");
                    const { frequency, period } = extractIntervalAndPeriod(resolution);
                    const startDate = unixTimestampToDate(periodParams.from);
                    const endDate = unixTimestampToDate(periodParams.to);
                    let noData = false;
                    const data = await this.retrieveData(parsedSymbol.code, startDate, endDate, frequency, period, periodParams.countBack);
                    if (data.length < 1 || data == null || data === []) { noData = true; }
                    resolve({
                                bars: data,
                                meta: {
                                    noData
                                }
                            });
                    // @ts-ignore
                } catch (e: unknown) {
                    if (e instanceof Error || typeof e === "string") {
                        const reasonString = getErrorMessage(e);
                        // tslint:disable-next-line:no-console
                        console.warn(
                            `HistoryProvider: getBars() failed, error=${reasonString}`
                        );
                        reject(reasonString);
                    }
                }
            }
        );
    }
}
